import AnalyticsIcon from "@mui/icons-material/Analytics";
import GetAppIcon from "@mui/icons-material/GetApp";
import GroupIcon from "@mui/icons-material/Group";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import Box from "@mui/joy/Box";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import TabPanel from "@mui/joy/TabPanel";
import Tabs from "@mui/joy/Tabs";
import Typography from "@mui/joy/Typography";
import React, { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Loading } from "../../../shared/components/feedback/loading/Loading";
import { PersonId } from "../../../models/person";
import { Question, Questionnaire } from "../../../models/questionnaire";
import {
  Participant,
  ParticipantByState,
  ParticipantId,
  ParticipantProgress,
  PutParticipantRequest,
  PutStudyRequest,
  Study,
  StudyId,
} from "../../../models/study";
import { generateUnixTimestamp } from "../../../shared/utils/time";
import QuestionnaireDrawer from "../components/QuestionnaireDrawer";
import StudyAddParticipantDrawer from "../components/StudyAddParticipantDrawer";
import StudyCohortDrawer from "../components/StudyCohortDrawer";
import StudyParticipantCSVDrawer from "../components/StudyParticipantCSVDrawer";
import StudyParticipantDrawer from "../components/StudyParticipantDrawer";
import ParticipantsTable from "../components/ParticipantsTable";
import DataQualityTab from "../components/DataQualityTab";
import ExportTab from "../components/ExportTab";
import OverviewTab from "../components/OverviewTab";
import StudyEditDrawer from "../components/StudyEditDrawer";
import {
  QuestionAnsweringClientContext,
  StudyQuartzClientContext,
} from "../../../shared/services/api/contexts";

export const StudyContext = React.createContext<Study>(null as unknown as Study);

export const getParticipantCurrentState = (participant: Participant) => {
  const { progress } = participant;
  if (progress.length === 0) {
    return undefined;
  }

  return progress.reduce(
    (max, current) => (new Date(max.start_date) > new Date(current.start_date) ? max : current),
    progress[0]
  );
};

export default function StudyPage() {
  const location = useLocation();
  const studyId = location.pathname.split("/")[2] as StudyId;
  const studyQuartzClient = React.useContext(StudyQuartzClientContext);
  const questionAnsweringQuartzClient = React.useContext(QuestionAnsweringClientContext);
  const [study, setStudy] = React.useState<Study | null>(null);
  const [participants, setParticipants] = React.useState<Participant[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<ParticipantId[]>([]);
  const [participantByState, setParticipantByState] = React.useState<ParticipantByState[]>([]);
  const [openEditStudy, setOpenEditStudy] = React.useState(false);
  const [openCohortDrawer, setOpenCohortDrawer] = React.useState(false);
  const [refreshTime, setRefreshTime] = React.useState(new Date().getTime());
  const [openAddParticipant, setOpenAddParticipant] = React.useState(false);
  const [openCSVImport, setOpenCSVImport] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<string | number | null>("overview");
  const [questionnaire, setQuestionnaire] = React.useState<Questionnaire | null>(null);
  const [openQuestionnaireEdit, setOpenQuestionnaireEdit] = React.useState(false);

  // Derived state using useMemo rather than an extra useEffect.
  const selectedParticipants = useMemo(() => {
    return selected.length > 0
      ? participants.filter((p) => selected.includes(p.participant_id))
      : [];
  }, [selected, participants]);

  // Refresh function to re-fetch study data and reset modal states.
  const refresh = useCallback(() => {
    setOpenCohortDrawer(false);
    setSelected([]);
    setOpenEditStudy(false);
    setRefreshTime(new Date().getTime());
  }, []);

  const handleUpdateStudy = async (study: Study) => {
    const updateStudyRequest = {
      title: study.title,
      cohort_id: study.cohort_id,
      acronym: study.acronym,
      description: study.description,
      phase: study.phase,
      questionnaire: study.questionnaire,
    } as PutStudyRequest;
    try {
      await studyQuartzClient.updateStudy(study.study_id, updateStudyRequest);
      refresh();
    } catch (err: any) {
      console.error(err.message);
    }
  };

  const handleSaveQuestionnaire = async (questionList: Question[]) => {
    if (!study) return;

    try {
      if (questionnaire) {
        await questionAnsweringQuartzClient.updateQuestionnaire(questionnaire.questionnaire_id, {
          ...questionnaire,
          item: questionList,
        });
      } else {
        const newQuestionnaire = await questionAnsweringQuartzClient.saveQuestionnaire({
          title: study.acronym || "New Questionnaire",
          description: "Newly created questionnaire",
          item: questionList,
        });
        handleUpdateStudy({ ...study, questionnaire: newQuestionnaire.questionnaire_id } as Study);
      }
      refresh();
    } catch (err: any) {
      console.error(err.message);
    }
  };

  const onParticipantAdded = async (personId: PersonId) => {
    const putParticipantRequest = {
      person_id: personId,
      study_id: studyId,
      progress: [
        {
          state: "screening",
          start_date: generateUnixTimestamp(),
        } as ParticipantProgress,
      ],
    } as PutParticipantRequest;
    try {
      await studyQuartzClient.postParticipant(putParticipantRequest);
      refresh();
    } catch (err: any) {
      console.error(err.message);
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await studyQuartzClient.getStudyById(studyId);
        setStudy(response);
        const participantsResponse = await studyQuartzClient.getParticipantsByStudyId(studyId);
        setParticipants(participantsResponse.entry as Participant[]);
        const studyStats = await studyQuartzClient.getStudyStats(studyId);
        setParticipantByState(studyStats.participant_by_state);
        if (response.questionnaire) {
          const questionsResponse = await questionAnsweringQuartzClient.getQuestionnaire(
            response.questionnaire
          );
          setQuestionnaire(questionsResponse);
        }
      } catch (e: any) {
        console.error(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [studyQuartzClient, studyId, refreshTime, questionAnsweringQuartzClient]);

  const toggleCohortDrawer = useCallback(() => {
    setOpenCohortDrawer((prev) => !prev);
  }, []);

  if (study === null || loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <StudyContext.Provider value={study}>
        {study.acronym ? (
          <Typography level="h2">{study.acronym}</Typography>
        ) : (
          <Typography sx={{ mt: 1, maxWidth: "80%", minWidth: "50%" }}>{study.title}</Typography>
        )}
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => {
            setActiveTab(newValue);
            setOpenCohortDrawer(false);
            setSelected([]);
            setOpenAddParticipant(false);
            setOpenCSVImport(false);
            setOpenQuestionnaireEdit(false);
            setOpenEditStudy(false);
          }}
          sx={{
            bgcolor: "transparent",
            mt: 2,
          }}
        >
          <TabList
            tabFlex={1}
            size="sm"
            sx={{
              justifyContent: "left",
              [`&& .${tabClasses.root}`]: {
                fontWeight: "600",
                flex: "initial",
                color: "text.tertiary",
                [`&.${tabClasses.selected}`]: {
                  bgcolor: "transparent",
                  color: "text.primary",
                  "&::after": {
                    height: "2px",
                    bgcolor: "primary.500",
                  },
                },
              },
            }}
          >
            <Tab sx={{ borderRadius: "6px 6px 0 0" }} value="overview">
              <ListItemDecorator>
                <AnalyticsIcon fontSize="small" />
              </ListItemDecorator>
              Overview
            </Tab>
            <Tab sx={{ borderRadius: "6px 6px 0 0", mx: 1 }} indicatorInset value="participants">
              <ListItemDecorator>
                <GroupIcon fontSize="small" />
              </ListItemDecorator>
              Participants
            </Tab>
            {participants.length > 0 && (
              <Tab sx={{ borderRadius: "6px 6px 0 0", mx: 1 }} indicatorInset value="dataQuality">
                <ListItemDecorator>
                  <TaskAltIcon fontSize="small" />
                </ListItemDecorator>
                Data Quality
              </Tab>
            )}
            {participants.length > 0 && (
              <Tab sx={{ borderRadius: "6px 6px 0 0", mx: 1 }} indicatorInset value="export">
                <ListItemDecorator>
                  <GetAppIcon fontSize="small" />
                </ListItemDecorator>
                Export
              </Tab>
            )}
          </TabList>
          <TabPanel value="overview">
            <OverviewTab
              study={study}
              participantByState={participantByState}
              toggleCohortDrawer={toggleCohortDrawer}
              handleUpdateStudy={handleUpdateStudy}
              setEditStudy={setOpenEditStudy}
            />
          </TabPanel>
          <TabPanel value="participants">
            <ParticipantsTable
              participants={participants}
              selected={selected}
              setSelected={setSelected}
              onAddParticipant={() => setOpenAddParticipant(true)}
              onCSVImport={() => setOpenCSVImport(true)}
            />
          </TabPanel>
          <TabPanel value="dataQuality">
            <DataQualityTab />
          </TabPanel>
          <TabPanel value="export">
            <ExportTab
              participants={participants}
              questionnaire={questionnaire}
              onQuestionnaireEdit={() => setOpenQuestionnaireEdit(true)}
            />
          </TabPanel>
        </Tabs>

        {/* Conditional Drawers and Modals */}
        {openCohortDrawer && (
          <StudyCohortDrawer
            study={study}
            onClose={() => setOpenCohortDrawer(false)}
            handleUpdateStudy={handleUpdateStudy}
          />
        )}
        {selected.length > 0 && (
          <StudyParticipantDrawer
            selectedIds={selected}
            selectedParticipants={selectedParticipants}
            participantByState={participantByState}
            closeModal={() => setSelected([])}
            refresh={refresh}
          />
        )}
        {openAddParticipant && (
          <StudyAddParticipantDrawer
            onClose={() => setOpenAddParticipant(false)}
            onParticipantAdded={onParticipantAdded}
          />
        )}
        {openCSVImport && (
          <StudyParticipantCSVDrawer
            studyId={study.study_id}
            onClose={() => setOpenCSVImport(false)}
            onCSVImported={refresh}
          />
        )}
        {openQuestionnaireEdit && (
          <QuestionnaireDrawer
            onClose={() => setOpenQuestionnaireEdit(false)}
            questions={questionnaire?.item || []}
            onSave={handleSaveQuestionnaire}
          />
        )}
        {openEditStudy && (
          <StudyEditDrawer onClose={() => setOpenEditStudy(false)} refresh={refresh} />
        )}
      </StudyContext.Provider>
    </Box>
  );
}
